// src/components/HandleHelpModal.tsx
// Shared diagnostic flow for users who can't view or confirm a social handle.
// Used in both Crushhh-List (CrushList) and Crushhh-IDs (ContactPointsPanel).
// Flow: what went wrong → screenshot guide → find their handle → confirm.
// "Private" and "Something else" short-circuit to static messages at Step 1.

import { useState } from 'react';

const FONT_DISPLAY = "'Fredoka', sans-serif";
const FONT_BODY    = "'Nunito', sans-serif";
const INK          = "#1A1A1A";
const CORAL        = "#E05A3A";
const CREAM        = "#F0DFC8";

type Platform = "instagram" | "facebook" | "snapchat" | "tiktok" | "x" | "linkedin";

interface Props {
  platform: Platform | string | null;
  handle: string;
  onConfirmed: () => void;
  onReEnter: () => void;
  onDismiss: () => void;
}

const ISSUE_OPTIONS = [
  "It took me to the wrong person",
  "I got a blank or error screen",
  "The account was private",
  "Something else",
] as const;

const ORDERED_PLATFORMS: Platform[] = [
  "instagram", "snapchat", "tiktok", "facebook",
];

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook:  "Facebook",
  snapchat:  "Snapchat",
  tiktok:    "TikTok",
  x:         "X",
  linkedin:  "LinkedIn",
};

const DEVICES = ["iOS", "Android", "Desktop"] as const;

function normalizePlatform(p: string | null): Platform {
  if (p && (ORDERED_PLATFORMS as string[]).includes(p)) return p as Platform;
  return "instagram";
}

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "12px 24px",
  fontFamily: FONT_DISPLAY,
  fontSize: 18,
  fontWeight: 700,
  background: CORAL,
  color: "#FFFFFF",
  border: `3px solid ${INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${INK}`,
  cursor: "pointer",
  transition: "all 0.1s ease",
};

const secondaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "11px 24px",
  fontFamily: FONT_DISPLAY,
  fontSize: 18,
  fontWeight: 700,
  background: "#FFFFFF",
  color: INK,
  border: `3px solid ${INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${INK}`,
  cursor: "pointer",
  transition: "all 0.1s ease",
};

export function HandleHelpModal({ platform, handle, onConfirmed, onReEnter, onDismiss }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [staticMsg, setStaticMsg] = useState<"private" | "other" | null>(null);
  const [activePlatform, setActivePlatform] = useState<Platform>(normalizePlatform(platform));
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  const platformLabel =
    platform && PLATFORM_LABELS[platform as Platform]
      ? PLATFORM_LABELS[platform as Platform]
      : platform
        ? platform.charAt(0).toUpperCase() + platform.slice(1)
        : "the app";

  const handleNext = () => {
    if (selectedIssue === "The account was private") {
      setStaticMsg("private");
      return;
    }
    if (selectedIssue === "Something else") {
      setStaticMsg("other");
      return;
    }
    setStep(2);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#FFFFFF",
          border: `3px solid ${INK}`,
          borderRadius: 20,
          boxShadow: `6px 6px 0px ${INK}`,
          padding: 28,
          width: "100%",
          maxWidth: 420,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={onDismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
            color: INK,
            lineHeight: 1,
            padding: 4,
            zIndex: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* ── Step 1: What went wrong? ── */}
        {step === 1 && !staticMsg && (
          <>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 20px" }}>
              What went wrong?
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {ISSUE_OPTIONS.map(opt => (
                <button
                  key={opt}
                  onClick={() => setSelectedIssue(opt)}
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 15,
                    fontWeight: 600,
                    textAlign: "left",
                    padding: "10px 16px",
                    borderRadius: 100,
                    border: `2px solid ${selectedIssue === opt ? CORAL : INK}`,
                    background: selectedIssue === opt ? CORAL : "#FFFFFF",
                    color: selectedIssue === opt ? "#FFFFFF" : INK,
                    cursor: "pointer",
                    transition: "all 0.12s ease",
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={!selectedIssue}
              style={{
                ...primaryBtn,
                background: selectedIssue ? CORAL : "#ccc",
                border: `3px solid ${selectedIssue ? INK : "#999"}`,
                boxShadow: selectedIssue ? `4px 4px 0px ${INK}` : "none",
                cursor: selectedIssue ? "pointer" : "default",
              }}
            >
              Next →
            </button>
          </>
        )}

        {/* ── Static: Private account ── */}
        {staticMsg === "private" && (
          <>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 16px" }}>
              Double-check the handle
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#555", lineHeight: 1.5, margin: "0 0 24px" }}>
              The handle you entered may not be quite right. Go back and make sure you copied
              it exactly — including any dots, underscores, or numbers.
            </p>
            <button
              onClick={() => { setStaticMsg(null); setStep(2); }}
              style={primaryBtn}
            >
              Show me where to find it →
            </button>
          </>
        )}

        {/* ── Static: Something else ── */}
        {staticMsg === "other" && (
          <>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 16px" }}>
              Let's find the right handle
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#555", lineHeight: 1.5, margin: "0 0 24px" }}>
              The most common issue is a handle that's slightly off. Let's make sure you're
              copying it from the right place.
            </p>
            <button
              onClick={() => { setStaticMsg(null); setStep(2); }}
              style={primaryBtn}
            >
              Show me where to find it →
            </button>
          </>
        )}

        {/* ── Step 2: Screenshot guide ── */}
        {step === 2 && (
          <>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 6px" }}>
              Where to find your handle
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: "#777", margin: "0 0 16px" }}>
              Tap your platform and device below.
            </p>

            {/* Platform tabs */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {ORDERED_PLATFORMS.map(p => (
                <button
                  key={p}
                  onClick={() => setActivePlatform(p)}
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 13,
                    fontWeight: 700,
                    padding: "5px 14px",
                    borderRadius: 100,
                    border: `2px solid ${activePlatform === p ? CORAL : INK}`,
                    background: activePlatform === p ? CORAL : "#FFFFFF",
                    color: activePlatform === p ? "#FFFFFF" : INK,
                    cursor: "pointer",
                    transition: "all 0.1s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {PLATFORM_LABELS[p]}
                </button>
              ))}
            </div>

            {/* Device cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 24 }}>
              {DEVICES.map(device => {
                const imgKey = `${activePlatform}-${device.toLowerCase()}`;
                const src = `/handle-guides/${imgKey}.webp`;
                const failed = imgErrors.has(imgKey);
                return (
                  <div key={device}>
                    <p style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: INK, margin: "0 0 8px" }}>
                      {device}
                    </p>
                    {failed ? (
                      <div
                        style={{
                          width: "100%",
                          height: 200,
                          background: CREAM,
                          border: `3px solid ${INK}`,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: INK,
                          fontFamily: FONT_BODY,
                          fontSize: 15,
                          fontWeight: 600,
                        }}
                      >
                        Screenshot coming soon
                      </div>
                    ) : (
                      <img
                        src={src}
                        alt={`${activePlatform} ${device} screenshot`}
                        onError={() => setImgErrors(prev => new Set(prev).add(imgKey))}
                        style={{
                          width: "100%",
                          borderRadius: 12,
                          border: `3px solid ${INK}`,
                          display: "block",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => setStep(3)} style={primaryBtn}>
                That's correct, continue →
              </button>
              <button onClick={() => setStep(1)} style={secondaryBtn}>
                Go back
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Find their exact handle ── */}
        {step === 3 && (
          <>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, color: INK, margin: "0 0 16px" }}>
              Find their exact handle
            </h2>

            <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: "#555", lineHeight: 1.5, margin: "0 0 16px" }}>
              Open {platformLabel} and go directly to your crush's profile. Their handle is
              the exact username shown below their name or in their profile URL — including
              any dots, underscores, or numbers. Double-check it matches what you entered exactly.
            </p>

            <div
              style={{
                fontFamily: "'Courier New', monospace",
                fontSize: 16,
                fontWeight: 700,
                background: CREAM,
                border: `2px solid ${INK}`,
                borderRadius: 10,
                padding: "10px 16px",
                marginBottom: 8,
                wordBreak: "break-all",
                color: INK,
              }}
            >
              {handle}
            </div>

            <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: INK, opacity: 0.6, marginTop: 0, marginBottom: 16 }}>
              Don't worry about the full URL — we'll trim it to just the username for matching.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={onConfirmed} style={primaryBtn}>
                Yes, add them
              </button>
              <button onClick={onReEnter} style={secondaryBtn}>
                Re-enter handle
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
